'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useShellViewport } from '@/hooks/useMediaQuery';
import type { ContextRailContent } from '@/lib/shell/contextRailModel';
import {
  getSurfaceMode,
  readRailPrefs,
  recordRailToggle,
  resolveRailOpen,
  setSurfaceMode,
  writeRailPrefs,
  type RailPreference,
  type ShellSurface,
} from '@/lib/shell/railPreferences';

export type ShellRailMode = 'pinned' | 'overlay' | 'hidden';

type ShellRailContextValue = {
  surface: ShellSurface;
  routeKey: string;
  /** True when the current route has registered rail content. */
  available: boolean;
  /** How the rail presents at the current viewport (pinned >=1440, overlay 1024-1439 and mobile sheet, hidden = no content). */
  mode: ShellRailMode;
  open: boolean;
  content: ContextRailContent | null;
  preference: RailPreference;
  setPreference: (preference: RailPreference) => void;
  toggleRail: () => void;
  openRail: () => void;
  closeRail: () => void;
};

type RegistryContextValue = {
  register: (content: ContextRailContent) => void;
  unregister: (key: string) => void;
};

const ShellRailContext = createContext<ShellRailContextValue | null>(null);
const ShellRailRegistryContext = createContext<RegistryContextValue | null>(null);

export function useShellRail(): ShellRailContextValue {
  const value = useContext(ShellRailContext);
  if (!value) throw new Error('useShellRail must be used inside ShellRailProvider');
  return value;
}

/**
 * Pages (or null-rendering bridge components inside them) publish rail content
 * upward with this hook. Content is built from data the page already owns — the
 * rail itself never fetches. Unregistering on unmount plus the provider's
 * route-key guard guarantees a navigation can never show a stale inspector.
 */
export function useRegisterContextRail(content: ContextRailContent | null): void {
  const registry = useContext(ShellRailRegistryContext);
  if (!registry) throw new Error('useRegisterContextRail must be used inside ShellRailProvider');
  const { register, unregister } = registry;

  useEffect(() => {
    if (!content) return;
    register(content);
    return () => unregister(content.key);
  }, [content, register, unregister]);
}

export function ShellRailProvider({
  surface,
  children,
}: {
  surface: ShellSurface;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const section = searchParams.get('section');
  const routeKey = section ? pathname + '?section=' + section : pathname;
  const viewport = useShellViewport();

  const [registered, setRegistered] = useState<{
    routeKey: string;
    content: ContextRailContent;
  } | null>(null);
  const [preference, setPreferenceState] = useState<RailPreference>('remember');
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const routeKeyRef = useRef(routeKey);
  routeKeyRef.current = routeKey;

  // Read the persisted preference before paint (post-hydration) so there is no
  // server/client mismatch and no visible open/close pop-in.
  useLayoutEffect(() => {
    const prefs = readRailPrefs();
    setPreferenceState(getSurfaceMode(prefs, surface));
    setPinnedOpen(resolveRailOpen(prefs, surface, routeKeyRef.current));
  }, [surface]);

  // Route change: re-resolve the pinned state for the new route and always close
  // the overlay (an inspector should never survive a navigation half-open).
  useLayoutEffect(() => {
    const prefs = readRailPrefs();
    setPinnedOpen(resolveRailOpen(prefs, surface, routeKey));
    setOverlayOpen(false);
  }, [routeKey, surface]);

  const register = useCallback((content: ContextRailContent) => {
    setRegistered({ routeKey: routeKeyRef.current, content });
  }, []);

  const unregister = useCallback((key: string) => {
    setRegistered((current) => (current && current.content.key === key ? null : current));
  }, []);

  // Stale-selection guard: content registered under a previous route never renders.
  const content = registered && registered.routeKey === routeKey ? registered.content : null;
  const available = content !== null;

  const mode: ShellRailMode = !available ? 'hidden' : viewport === 'wide' ? 'pinned' : 'overlay';
  const open = mode === 'pinned' ? pinnedOpen : mode === 'overlay' ? overlayOpen : false;

  const setPreference = useCallback(
    (next: RailPreference) => {
      setPreferenceState(next);
      const prefs = setSurfaceMode(readRailPrefs(), surface, next);
      writeRailPrefs(prefs);
      if (next === 'always') setPinnedOpen(true);
      if (next === 'collapsed') setPinnedOpen(false);
    },
    [surface]
  );

  const applyOpenState = useCallback(
    (next: boolean) => {
      if (viewport === 'wide') {
        setPinnedOpen(next);
        writeRailPrefs(recordRailToggle(readRailPrefs(), surface, routeKeyRef.current, next));
      } else {
        setOverlayOpen(next);
      }
    },
    [surface, viewport]
  );

  const toggleRail = useCallback(() => applyOpenState(!open), [applyOpenState, open]);
  const openRail = useCallback(() => applyOpenState(true), [applyOpenState]);
  const closeRail = useCallback(() => applyOpenState(false), [applyOpenState]);

  const registryValue = useMemo(() => ({ register, unregister }), [register, unregister]);
  const value = useMemo(
    () => ({
      surface,
      routeKey,
      available,
      mode,
      open,
      content,
      preference,
      setPreference,
      toggleRail,
      openRail,
      closeRail,
    }),
    [
      surface,
      routeKey,
      available,
      mode,
      open,
      content,
      preference,
      setPreference,
      toggleRail,
      openRail,
      closeRail,
    ]
  );

  return (
    <ShellRailContext.Provider value={value}>
      <ShellRailRegistryContext.Provider value={registryValue}>
        {children}
      </ShellRailRegistryContext.Provider>
    </ShellRailContext.Provider>
  );
}
